import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, Info, Mail, DollarSign, HelpCircle, Home } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { BUSINESS_CONFIG } from '../config/businessConfig';

const Navigation = () => {
  const location = useLocation();
  const [platformLogo, setPlatformLogo] = useState(null);

  useEffect(() => {
    const fetchPlatformLogo = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'platform_logo')
          .maybeSingle();

        if (data?.value) {
          const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
          if (parsed?.logo_url) {
            setPlatformLogo(parsed.logo_url);
          }
        }
      } catch (error) {
        // Silently fall back
      }
    };
    fetchPlatformLogo();
  }, []);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/pricing', label: 'Pricing', icon: DollarSign },
    { path: '/faq', label: 'FAQ', icon: HelpCircle },
    { path: '/grant-tool', label: 'Grant Tool', icon: FileText },
    { path: '/about', label: 'About Us', icon: Info },
    { path: '/contact', label: 'Contact', icon: Mail }
  ];

  return (
    <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 w-full">
          {/* Left section: Logo and Desktop Nav Links */}
          <div className="flex items-center space-x-12">
            <Link to="/" className={`flex items-center ${!platformLogo ? 'space-x-2' : ''} shrink-0`} data-testid="logo-link">
              {platformLogo ? (
                <img src={platformLogo} alt={BUSINESS_CONFIG.name} className="h-8 w-auto object-contain" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-lg border border-slate-200 shadow-[0_2px_4px_rgba(0,59,92,0.1)]"></div>
                  <span className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>{BUSINESS_CONFIG.name}</span>
                </>
              )}
            </Link>

            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${isActive
                      ? 'bg-[#003B5C] text-white'
                      : 'text-[#003B5C] hover:bg-[#003B5C]/5'
                      }`}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right section: Actions */}
          <div className="flex items-center space-x-4">
            {/* Member Login Button (Desktop only) */}
            <div className="hidden md:block">
              <Link
                to="/login"
                className="px-6 py-2 bg-gradient-to-r from-[#003B5C] to-[#74A57F] text-white rounded-full font-medium hover:shadow-lg transition-all"
                data-testid="member-login-button"
              >
                Member Login
              </Link>
            </div>

            {/* Mobile Menu - Simplified */}
            <div className="md:hidden">
              <Link
                to="/contact"
                className="flex items-center space-x-2 px-4 py-2 bg-[#003B5C] text-white rounded-lg font-medium"
                data-testid="mobile-contact"
              >
                <Mail className="w-4 h-4" />
                <span>Contact</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;